<template>
  <div class="merchant-panel">
    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon all-icon">🏪</div>
        <div class="stat-info">
          <div class="stat-value">{{ total }}</div>
          <div class="stat-label">商家总数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon on-icon">🟢</div>
        <div class="stat-info">
          <div class="stat-value">{{ enabledCount }}</div>
          <div class="stat-label">正常营业（本页）</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon off-icon">🔴</div>
        <div class="stat-info">
          <div class="stat-value">{{ list.length - enabledCount }}</div>
          <div class="stat-label">已禁用（本页）</div>
        </div>
      </div>
    </div>

    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <div class="search-wrapper">
          <span class="search-icon">🔍</span>
          <input
            v-model="keyword"
            type="text"
            placeholder="搜索店铺名称 / 账号 / 联系方式..."
            class="search-input-modern"
            @keyup.enter="load(1)"
          />
          <button class="search-btn" @click="load(1)">搜索</button>
        </div>
      </div>
      <div class="toolbar-right">
        <button class="add-btn" @click="openAdd">➕ 新增商家</button>
      </div>
    </div>

    <!-- 商家表格 -->
    <div class="table-wrapper" v-if="list.length > 0">
      <table class="admin-table">
        <thead>
          <tr>
            <th style="width: 60px">ID</th>
            <th>店铺 / 账号</th>
            <th style="width: 130px">类型 / 资料审核</th>
            <th style="width: 120px">联系方式</th>
            <th style="width: 110px">商品（上架）</th>
            <th style="width: 90px">订单数</th>
            <th style="width: 120px">销售额</th>
            <th style="width: 140px">货款（可用 / 在途）</th>
            <th style="width: 90px">状态</th>
            <th style="width: 230px">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in list" :key="row.id">
            <td>{{ row.id }}</td>
            <td>
              <div class="shop-cell">
                <span class="shop-name">{{ row.shop_name }}</span>
                <span class="official-tag" v-if="row.is_official === 1">官方自营</span>
                <div class="shop-sub">账号：{{ row.username }}</div>
                <div class="shop-sub" v-if="row.last_login_at">最近登录：{{ row.last_login_at }}</div>
              </div>
            </td>
            <td>
              <div class="shop-sub">{{ Number(row.merchant_type) === 1 ? '个人商家' : '企业商家' }}</div>
              <div class="audit-tag" :class="auditClass(row)">
                {{ auditText(row) }}
              </div>
            </td>
            <td>{{ row.contact || '-' }}</td>
            <td>{{ row.goods_count }}（{{ row.online_goods_count }}）</td>
            <td>{{ row.order_count }}</td>
            <td class="money">￥{{ formatMoney(row.sales_amount) }}</td>
            <td>
              <div class="money">￥{{ formatMoney(row.fund_balance) }}</div>
              <div class="shop-sub">在途 ￥{{ formatMoney(row.fund_pending) }}</div>
            </td>
            <td>
              <span class="status-tag" :class="row.status === 1 ? 'on-sale' : 'off-sale'">
                {{ row.status === 1 ? '正常' : '已禁用' }}
              </span>
            </td>
            <td>
              <div class="action-btns">
                <button class="action-btn edit" @click="openEdit(row)">编辑</button>
                <button
                  class="action-btn audit"
                  :class="{ highlight: Number(row.audit_status) === 0 || Number(row.qual_audit_status) === 0 }"
                  @click="openAudit(row)"
                >审核</button>
                <button class="action-btn" :class="row.status === 1 ? 'off' : 'on'" @click="toggle(row)">
                  {{ row.status === 1 ? '禁用' : '启用' }}
                </button>
                <button class="action-btn" @click="viewGoods(row)">商品</button>
                <button class="action-btn delete" @click="remove(row)">删除</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="table-footer">
        <span class="total-info">共 {{ totalPage || 1 }} 页 / {{ total }} 个商家</span>
        <div class="pagination">
          <button class="page-btn" :disabled="page <= 1" @click="load(page - 1)">上一页</button>
          <span class="page-num">{{ page }} / {{ totalPage || 1 }}</span>
          <button class="page-btn" :disabled="page >= totalPage" @click="load(page + 1)">下一页</button>
        </div>
      </div>
    </div>
    <div v-else class="empty-state">
      <span style="font-size: 48px">🏪</span>
      <p>暂无商家，点击「新增商家」邀请商家入驻</p>
    </div>

    <!-- 新增 / 编辑商家弹窗 -->
    <div v-if="showForm" class="modal-mask" @click.self="showForm = false">
      <div class="modal-card">
        <div class="modal-header">
          <span>{{ form.id ? '编辑商家' : '新增商家' }}</span>
          <span class="modal-close" @click="showForm = false">✕</span>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>登录账号 <span class="required">*</span></label>
            <input v-model.trim="form.username" type="text" placeholder="3-30 位字母 / 数字 / 下划线（用于登录商家后台与客服工作台）" />
          </div>
          <div class="form-group">
            <label>{{ form.id ? '重置密码（留空表示不修改）' : '登录密码' }} <span class="required" v-if="!form.id">*</span></label>
            <input v-model="form.password" type="text" placeholder="不少于 6 位" />
          </div>
          <div class="form-group">
            <label>店铺名称 <span class="required">*</span></label>
            <input v-model.trim="form.shop_name" type="text" placeholder="如：糖果服饰旗舰店" />
          </div>
          <div class="form-group">
            <label>店铺 Logo（图片地址）</label>
            <input v-model.trim="form.shop_logo" type="text" placeholder="可填图片空间中的图片地址" />
          </div>
          <div class="form-group">
            <label>店铺简介</label>
            <textarea v-model.trim="form.shop_desc" rows="2" placeholder="一句话介绍店铺"></textarea>
          </div>
          <div class="form-group">
            <label>联系方式</label>
            <input v-model.trim="form.contact" type="text" placeholder="手机号 / 微信号等" />
          </div>
          <div class="form-group">
            <label>商家类型 <span class="required">*</span></label>
            <select v-model.number="form.merchant_type">
              <option :value="2">企业商家（需营业执照）</option>
              <option :value="1">个人商家（实名认证：姓名 + 身份证）</option>
            </select>
          </div>
          <div class="form-group">
            <label>商家状态</label>
            <select v-model.number="form.status">
              <option :value="1">正常营业</option>
              <option :value="0">禁用（不可登录后台与工作台）</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn cancel" @click="showForm = false">取消</button>
          <button class="modal-btn confirm" :disabled="saving" @click="submit">{{ saving ? '保存中...' : '保存' }}</button>
        </div>
      </div>
    </div>

    <!-- 商家资料审核弹窗 -->
    <div v-if="auditVisible" class="modal-mask" @click.self="auditVisible = false">
      <div class="modal-card audit-card">
        <div class="modal-header">
          <span>商家资料审核 · {{ auditData.shop.shop_name || auditData.shop.username }}</span>
          <span class="modal-close" @click="auditVisible = false">✕</span>
        </div>
        <div class="modal-body">
          <div v-if="auditLoading" class="audit-loading">加载中...</div>

          <template v-else>
            <div class="audit-block">
              <div class="audit-block-title">店铺资料</div>
              <div class="audit-row"><span class="k">商家类型</span><span class="v">{{ Number(auditData.shop.merchant_type) === 1 ? '个人商家' : '企业商家' }}</span></div>
              <div class="audit-row"><span class="k">当前商城展示</span><span class="v">{{ auditData.shop.shop_name || '-' }}</span></div>
              <div class="audit-row"><span class="k">本次提交店铺名</span><span class="v">{{ auditData.shop.pending.shop_name || '（未修改）' }}</span></div>
              <div class="audit-row"><span class="k">本次提交 Logo</span><span class="v">
                <img v-if="auditData.shop.pending.shop_logo" :src="auditData.shop.pending.shop_logo" class="audit-thumb" alt="">
                <template v-else>（未修改）</template>
              </span></div>
              <div class="audit-row"><span class="k">本次提交简介</span><span class="v">{{ auditData.shop.pending.shop_desc || '（未修改）' }}</span></div>
              <div class="audit-row"><span class="k">资料审核状态</span><span class="v">{{ statusText(auditData.shop.audit_status) }}</span></div>
              <div class="audit-row" v-if="auditData.shop.audit_remark"><span class="k">上次驳回原因</span><span class="v warn">{{ auditData.shop.audit_remark }}</span></div>
            </div>

            <div class="audit-block">
              <div class="audit-block-title">
                {{ Number(auditData.qualification.qual_type) === 1 ? '个人资质（实名认证）' : '企业资质（营业执照）' }}
              </div>
              <div class="audit-row"><span class="k">{{ Number(auditData.qualification.qual_type) === 1 ? '姓名' : '企业名称' }}</span><span class="v">{{ auditData.qualification.subject_name || '-' }}</span></div>
              <div class="audit-row" v-if="Number(auditData.qualification.qual_type) !== 1"><span class="k">统一社会信用代码</span><span class="v">{{ auditData.qualification.credit_code || '-' }}</span></div>
              <div class="audit-row" v-if="Number(auditData.qualification.qual_type) !== 1"><span class="k">法定代表人</span><span class="v">{{ auditData.qualification.legal_name || '-' }}</span></div>
              <div class="audit-row"><span class="k">{{ Number(auditData.qualification.qual_type) === 1 ? '身份证号' : '法人证件号' }}</span><span class="v">{{ auditData.qualification.legal_id_no || '-' }}</span></div>
              <div class="audit-row"><span class="k">其他资质说明</span><span class="v">{{ auditData.qualification.other_desc || '-' }}</span></div>
              <div class="audit-row"><span class="k">资质审核状态</span><span class="v">{{ statusText(auditData.qualification.audit_status) }}</span></div>
              <div class="audit-images">
                <div class="audit-image" v-if="auditData.qualification.license_image">
                  <img :src="auditData.qualification.license_image" alt="">
                  <span>营业执照</span>
                </div>
                <div class="audit-image" v-if="auditData.qualification.legal_id_front">
                  <img :src="auditData.qualification.legal_id_front" alt="">
                  <span>证件（人像面）</span>
                </div>
                <div class="audit-image" v-if="auditData.qualification.legal_id_back">
                  <img :src="auditData.qualification.legal_id_back" alt="">
                  <span>证件（国徽面）</span>
                </div>
                <div class="audit-image" v-if="auditData.qualification.other_image">
                  <img :src="auditData.qualification.other_image" alt="">
                  <span>其他资质</span>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>审核备注（驳回时建议填写原因，商家可见）</label>
              <textarea v-model.trim="auditRemark" rows="2" placeholder="如：营业执照图片不清晰，请重新上传"></textarea>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="modal-btn cancel" @click="auditVisible = false">取消</button>
          <button class="modal-btn reject" :disabled="auditing" @click="submitAudit('reject')">驳回</button>
          <button class="modal-btn confirm" :disabled="auditing" @click="submitAudit('approve')">{{ auditing ? '提交中...' : '审核通过' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getMerchantListApi,
  saveMerchantApi,
  toggleMerchantApi,
  deleteMerchantApi,
  getMerchantShopDetailApi,
  auditMerchantApi
} from '@/api/merchant'

export default {
  name: 'MerchantPanel',
  data () {
    return {
      list: [],
      page: 1,
      pageSize: 20,
      total: 0,
      totalPage: 1,
      keyword: '',
      showForm: false,
      saving: false,
      form: {
        id: 0,
        username: '',
        password: '',
        shop_name: '',
        shop_logo: '',
        shop_desc: '',
        contact: '',
        merchant_type: 2,
        status: 1
      },
      // ===== 商家资料审核 =====
      auditVisible: false,
      auditLoading: false,
      auditing: false,
      auditRemark: '',
      auditData: {
        shop: { pending: {} },
        qualification: {}
      }
    }
  },
  computed: {
    enabledCount () {
      return this.list.filter(item => Number(item.status) === 1).length
    }
  },
  created () {
    this.load(1)
  },
  methods: {
    emptyForm () {
      return {
        id: 0,
        username: '',
        password: '',
        shop_name: '',
        shop_logo: '',
        shop_desc: '',
        contact: '',
        merchant_type: 2,
        status: 1
      }
    },
    formatMoney (val) {
      const num = Number(val) || 0
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async load (page) {
      this.page = Math.max(1, page || 1)
      try {
        const { data } = await getMerchantListApi(this.page, this.pageSize, this.keyword)
        this.list = (data && data.list) || []
        this.total = (data && data.total) || 0
        this.totalPage = (data && data.totalPage) || 1
      } catch (e) {
        console.error('商家列表加载失败', e)
      }
    },
    openAdd () {
      this.form = this.emptyForm()
      this.showForm = true
    },
    openEdit (row) {
      this.form = {
        id: row.id,
        username: row.username,
        password: '',
        shop_name: row.shop_name,
        shop_logo: row.shop_logo,
        shop_desc: row.shop_desc,
        contact: row.contact,
        merchant_type: Number(row.merchant_type) === 1 ? 1 : 2,
        status: Number(row.status)
      }
      this.showForm = true
    },
    async submit () {
      const f = this.form
      if (!f.username) { alert('请填写商家登录账号'); return }
      if (!f.shop_name) { alert('请填写店铺名称'); return }
      if (!f.id && (!f.password || f.password.length < 6)) { alert('请设置不少于 6 位的登录密码'); return }
      if (f.id && f.password && f.password.length < 6) { alert('密码长度不能少于 6 位'); return }

      this.saving = true
      try {
        const { message } = await saveMerchantApi(f)
        alert(message || '保存成功')
        this.showForm = false
        this.load(f.id ? this.page : 1)
      } catch (e) {
        console.error('保存商家失败', e)
      } finally {
        this.saving = false
      }
    },
    async toggle (row) {
      const action = Number(row.status) === 1 ? '禁用' : '启用'
      if (!confirm(`确定${action}商家「${row.shop_name}」吗？`)) return
      try {
        const { message } = await toggleMerchantApi(row.id)
        alert(message || '操作成功')
        this.load(this.page)
      } catch (e) {
        console.error('操作失败', e)
      }
    },
    async remove (row) {
      if (!confirm(`确定删除商家「${row.shop_name}」吗？删除后该账号无法恢复。`)) return
      try {
        const { message } = await deleteMerchantApi(row.id)
        alert(message || '删除成功')
        this.load(this.page)
      } catch (e) {
        console.error('删除失败', e)
      }
    },
    // ===== 商家资料审核 =====
    // 列表中「资料审核」文案 / 样式（店铺资料或资质任一待审/驳回都视为需处理）
    auditText (row) {
      const status = Number(row.audit_status)
      const qual = Number(row.qual_audit_status)
      if (status === 0 || qual === 0) return '待审核'
      if (status === 2 || qual === 2) return '已驳回'
      return '已通过'
    },
    auditClass (row) {
      const status = Number(row.audit_status)
      const qual = Number(row.qual_audit_status)
      if (status === 0 || qual === 0) return 'pending'
      if (status === 2 || qual === 2) return 'rejected'
      return 'passed'
    },
    statusText (status) {
      const s = Number(status)
      if (s === 0) return '待审核'
      if (s === 2) return '已驳回'
      return '已通过'
    },
    async openAudit (row) {
      this.auditVisible = true
      this.auditLoading = true
      this.auditRemark = ''
      this.auditData = { shop: { pending: {} }, qualification: {} }
      try {
        const { data } = await getMerchantShopDetailApi(row.id)
        this.auditData = {
          shop: (data && data.shop) || { pending: {} },
          qualification: (data && data.qualification) || {}
        }
      } catch (e) {
        console.error('加载商家审核资料失败', e)
      } finally {
        this.auditLoading = false
      }
    },
    async submitAudit (action) {
      const shop = this.auditData.shop || {}
      const merchantId = Number(shop.merchant_id) || 0
      if (!merchantId) {
        alert('商家信息加载失败，请重新打开审核弹窗')
        return
      }
      if (action === 'reject' && !this.auditRemark) {
        alert('请填写驳回原因，方便商家修改后重新提交')
        return
      }
      if (action === 'approve' && !confirm('确定通过该商家的店铺资料与资质吗？通过后将立即在商城展示。')) {
        return
      }
      this.auditing = true
      try {
        const { message } = await auditMerchantApi(merchantId, action, this.auditRemark)
        alert(message || '操作成功')
        this.auditVisible = false
        this.load(this.page)
      } catch (e) {
        console.error('审核失败', e)
      } finally {
        this.auditing = false
      }
    },
    // 查看该商家的商品（切换到「商品管理」并按商家筛选）
    viewGoods (row) {
      this.$emit('view-goods', row)
    }
  }
}
</script>

<style lang="less" scoped>
.merchant-panel {
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 16px;
  }

  .stat-card {
    background: #fff;
    border-radius: 10px;
    padding: 16px;
    display: flex;
    align-items: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

    .stat-icon {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      margin-right: 12px;
      background: #f5f7fa;
    }

    .stat-value { font-size: 22px; font-weight: 700; color: #222; }
    .stat-label { margin-top: 4px; font-size: 12px; color: #999; }
  }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
  }

  .search-wrapper {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 0 8px;

    .search-icon { color: #bbb; margin-right: 6px; }
    .search-input-modern {
      border: none;
      outline: none;
      height: 36px;
      width: 320px;
      font-size: 13px;
    }
    .search-btn {
      border: none;
      background: #ff6b35;
      color: #fff;
      border-radius: 6px;
      height: 28px;
      padding: 0 12px;
      cursor: pointer;
    }
  }

  .add-btn {
    background: linear-gradient(90deg, #ff6b35, #ff8c42);
    color: #fff;
    border: none;
    border-radius: 8px;
    height: 36px;
    padding: 0 16px;
    cursor: pointer;
  }

  .table-wrapper {
    background: #fff;
    border-radius: 10px;
    padding: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }

  .admin-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td {
      padding: 10px 8px;
      border-bottom: 1px solid #f2f2f2;
      text-align: left;
      vertical-align: middle;
    }

    th { color: #888; font-weight: 500; background: #fafafa; }

    .shop-name { font-weight: 600; color: #222; }
    .shop-sub { font-size: 12px; color: #999; margin-top: 2px; }
    .money { color: #ff6b35; font-weight: 600; }

    .official-tag {
      display: inline-block;
      margin-left: 6px;
      font-size: 11px;
      color: #ff6b35;
      border: 1px solid #ffd2b0;
      border-radius: 4px;
      padding: 0 4px;
    }

    // 资料审核状态标记
    .audit-tag {
      display: inline-block;
      margin-top: 4px;
      padding: 1px 8px;
      border-radius: 10px;
      font-size: 11px;

      &.pending { background: #fff7e6; color: #d46b08; }
      &.rejected { background: #fff1f0; color: #cf1322; }
      &.passed { background: #f6ffed; color: #389e0d; }
    }

    .status-tag {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 10px;
      font-size: 12px;

      &.on-sale { background: #e8f5e9; color: #43a047; }
      &.off-sale { background: #fdecea; color: #e53935; }
    }

    .action-btns {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      .action-btn {
        border: 1px solid #ddd;
        background: #fff;
        border-radius: 6px;
        height: 28px;
        padding: 0 10px;
        cursor: pointer;
        color: #666;

        &.edit { border-color: #4caf50; color: #4caf50; }
        &.audit { border-color: #1677ff; color: #1677ff; }
        &.audit.highlight { background: #1677ff; color: #fff; }
        &.on { border-color: #4caf50; color: #4caf50; }
        &.off { border-color: #ff9800; color: #ff9800; }
        &.delete { border-color: #e53935; color: #e53935; }
      }
    }
  }

  .table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;

    .total-info { font-size: 12px; color: #999; }
    .pagination {
      display: flex;
      align-items: center;
      gap: 8px;

      .page-btn {
        border: 1px solid #ddd;
        background: #fff;
        border-radius: 6px;
        height: 28px;
        padding: 0 10px;
        cursor: pointer;

        &:disabled { opacity: 0.5; cursor: not-allowed; }
      }

      .page-num { font-size: 12px; color: #666; }
    }
  }

  .empty-state {
    background: #fff;
    border-radius: 10px;
    padding: 60px 0;
    text-align: center;
    color: #999;
  }

  .modal-mask {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-card {
    width: 520px;
    max-height: 86vh;
    overflow-y: auto;
    background: #fff;
    border-radius: 12px;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid #f2f2f2;
    font-weight: 600;

    .modal-close { cursor: pointer; color: #999; }
  }

  .modal-body { padding: 14px 18px; }

  .form-group {
    margin-bottom: 12px;

    label { display: block; font-size: 13px; color: #666; margin-bottom: 6px; }
    .required { color: #e53935; }

    input, textarea, select {
      width: 100%;
      box-sizing: border-box;
      border: 1px solid #e2e2e2;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 13px;
      outline: none;

      &:focus { border-color: #ff6b35; }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 12px 18px;
    border-top: 1px solid #f2f2f2;

    .modal-btn {
      border-radius: 8px;
      height: 34px;
      padding: 0 18px;
      cursor: pointer;

      &.cancel { background: #f5f5f5; border: 1px solid #e2e2e2; color: #666; }
      &.confirm { background: linear-gradient(90deg, #ff6b35, #ff8c42); border: none; color: #fff; }
      &.reject { background: #fff; border: 1px solid #e53935; color: #e53935; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }

  // ===== 商家资料审核弹窗 =====
  .audit-card {
    width: 620px;
  }

  .audit-loading {
    padding: 30px 0;
    text-align: center;
    color: #999;
    font-size: 13px;
  }

  .audit-block {
    margin-bottom: 16px;
    padding: 12px 14px;
    border: 1px solid #f0f2f5;
    border-radius: 10px;
    background: #fafbfc;

    .audit-block-title {
      font-size: 13px;
      font-weight: 600;
      color: #1f2d3d;
      margin-bottom: 8px;
    }
  }

  .audit-row {
    display: flex;
    font-size: 13px;
    line-height: 22px;
    padding: 2px 0;

    .k {
      width: 110px;
      flex-shrink: 0;
      color: #98a2b3;
    }

    .v {
      flex: 1;
      color: #1f2d3d;
      word-break: break-all;

      &.warn { color: #cf1322; }
    }
  }

  .audit-thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 6px;
    vertical-align: middle;
  }

  .audit-images {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;

    .audit-image {
      width: 96px;
      text-align: center;

      img {
        width: 96px;
        height: 96px;
        object-fit: cover;
        border-radius: 8px;
        background: #f5f6f8;
        cursor: pointer;
      }

      span {
        display: block;
        margin-top: 4px;
        font-size: 11px;
        color: #98a2b3;
      }
    }
  }
}
</style>
